// Per-companion analytics shown in the editor's "Insights" tab: call volume /
// sentiment timeseries plus a compact recent-calls list, all guideId-scoped.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  getTimeseries,
  listCalls,
  type CallRecord,
  type TimeseriesPoint,
} from "../../services/analytics";
import { getEvalSummary, type EvalSummary } from "../../services/evals";
import { RubricSummary } from "../evals/RubricSummary";
import { CallsBarChart, SentimentLineChart } from "./Charts";
import { InputModeToggle, RangePicker, RecentCallsList } from "./bits";
import {
  apiInputMode,
  fillDays,
  windowFromPreset,
  type InputModeFilter,
  type RangePreset,
} from "./format";

export function GuideInsights({ guideId }: { guideId: string }) {
  const navigate = useNavigate();
  const [preset, setPreset] = useState<RangePreset>("30d");
  const [mode, setMode] = useState<InputModeFilter>("all");
  const [series, setSeries] = useState<TimeseriesPoint[]>([]);
  const [recent, setRecent] = useState<CallRecord[]>([]);
  const [evals, setEvals] = useState<EvalSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const win = useMemo(() => windowFromPreset(preset), [preset]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const m = apiInputMode(mode);
    Promise.allSettled([
      getTimeseries({ guideId, from: win.from, to: win.to, input_mode: m }),
      listCalls({ guideId, from: win.from, to: win.to, input_mode: m, limit: 10 }),
      getEvalSummary(guideId, { from: win.from, to: win.to }),
    ]).then(([ts, calls, evalSummary]) => {
      if (cancelled) return;
      setSeries(ts.status === "fulfilled" ? ts.value.items : []);
      setRecent(calls.status === "fulfilled" ? calls.value.items : []);
      setEvals(evalSummary.status === "fulfilled" ? evalSummary.value : null);
      if (ts.status === "rejected") {
        const reason = ts.reason;
        setError(reason instanceof Error ? reason.message : "Analytics unavailable");
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [guideId, win, mode]);

  const filled = useMemo(() => fillDays(series, win.from, win.to), [series, win]);

  return (
    <div className="guide-insights">
      <div className="insights-toolbar">
        <InputModeToggle value={mode} onChange={setMode} />
        <RangePicker value={preset} onChange={setPreset} />
      </div>

      {error && <div className="callout">Analytics unavailable ({error}).</div>}

      {loading ? (
        <div className="state-box">Loading insights…</div>
      ) : (
        <>
          {evals && evals.count > 0 && (
            <>
              <div className="panel-head">
                Rubric quality · {evals.rubricId}
                <span className="spacer" />
                <Link
                  className="btn btn-ghost btn-sm"
                  to={`/insights?guideId=${encodeURIComponent(guideId)}`}
                >
                  Full evals →
                </Link>
              </div>
              <RubricSummary
                overallAvg={evals.overallAvg}
                count={evals.count}
                criteria={evals.criteria}
                rubric={[]}
              />
            </>
          )}
          <div className="panel-head">Calls per day</div>
          <CallsBarChart points={filled} />
          <div className="panel-head">Avg sentiment</div>
          <SentimentLineChart points={filled} />
          <div className="panel-head">
            Recent calls
            <span className="spacer" />
            <Link className="btn btn-ghost btn-sm" to={`/calls?guideId=${encodeURIComponent(guideId)}`}>
              View all →
            </Link>
          </div>
          <RecentCallsList
            calls={recent}
            onOpen={(c) =>
              navigate(
                `/calls?guideId=${encodeURIComponent(guideId)}&call=${encodeURIComponent(c.id)}`,
              )
            }
            emptyText="No calls for this companion in the selected range."
          />
        </>
      )}
    </div>
  );
}
